import _ from 'lodash';

import { formatPriorToDisplayName } from 'constants/condition';
import { TaskAssignmentType } from 'constants/tasks';
import { getPersonFullName } from 'utils/loanApplication';

const resolveTask = ({ task, taskTypesById, borrowersById, key, index }) => {
  const taskType = task.taskTypeId ? taskTypesById?.[task.taskTypeId] : null;
  const assignee = task.assigneeId ? borrowersById?.[task.assigneeId] : null;

  return {
    key: `${key}-task-${index}`,
    // taskTypeId/assigneeId are kept (not just the display names) so the
    // confirmation gate can re-bind a task's assignee (LEN-33). Only a
    // Borrower-assigned task is reassignable — SLA-group/other tasks resolve
    // their owner from config, so the gate offers no assignee picker for them.
    taskTypeId: task.taskTypeId ?? null,
    assigneeId: task.assigneeId ?? null,
    isBorrowerAssigned: taskType?.taskAssignmentType === TaskAssignmentType.BORROWER,
    name: taskType?.name ?? null,
    assigneeName: assignee ? getPersonFullName(assignee) : null,
  };
};

// LEN-33: the borrower choices for a task-assignee picker at the gate, resolved
// once from the loan's borrowers so every condition card shares the same list.
const buildBorrowerOptions = borrowersById =>
  Object.values(borrowersById ?? {}).map(borrower => ({
    id: borrower.borrowerId,
    name: getPersonFullName(borrower),
  }));

// The add_conditions_from_library tool identifies a condition by its NUMBER
// (e.g. "213"), not the GUID id the AdminPanel condition store is keyed
// on — so match on `number` first, then fall back to the GUID key for any caller
// that does send the GUID. Prefer an enabled condition when the same number is
// duplicated (dev smoke data leaves stale rows with the same number).
export const buildConditionsByNumber = conditionsById => {
  const byNumber = {};

  Object.values(conditionsById ?? {}).forEach(condition => {
    if (_.isNil(condition?.number)) {
      return;
    }

    const numberKey = String(condition.number);
    const existing = byNumber[numberKey];

    if (!existing || (condition.isEnabled && !existing.isEnabled)) {
      byNumber[numberKey] = condition;
    }
  });

  return byNumber;
};

// Turn the id-only library entries (extractLibraryConditions) into the display
// shape ConditionReviewItem renders — resolving the condition (by number, GUID
// fallback) to its library wording/prior-to (an override wins) and each task's
// taskTypeId/assigneeId to a task-type name and borrower name. Kept as a pure
// function so it is unit-testable without the store.
export const resolveLibraryConditions = ({
  libraryConditions = [],
  conditionsById,
  conditionsByNumber,
  taskTypesById,
  borrowersById,
}) => {
  const borrowerOptions = buildBorrowerOptions(borrowersById);

  return libraryConditions.map(libraryCondition => {
    const {
      conditionId,
      index,
      key,
      tasks,
      messageOverride,
      borrowerMessageOverride,
      priorToOverrideRaw,
    } = libraryCondition;
    const definition = conditionsByNumber?.[conditionId] ?? conditionsById?.[conditionId] ?? null;

    const internalText = messageOverride ?? definition?.message ?? null;
    const borrowerText = borrowerMessageOverride ?? definition?.borrowerMessage ?? null;
    const priorToRaw = priorToOverrideRaw ?? definition?.priorToType ?? null;

    return {
      index,
      key,
      isLibrary: true,
      conditionId,
      internalText,
      borrowerText,
      priorToRaw,
      priorToTypeId: priorToOverrideRaw ? null : (definition?.priorToTypeId ?? null),
      priorTo: priorToRaw ? formatPriorToDisplayName(priorToRaw) : null,
      borrowerOptions,
      tasks: tasks.map((task, taskIndex) =>
        resolveTask({ task, taskTypesById, borrowersById, key, index: taskIndex }),
      ),
    };
  });
};
